import { useEffect, useRef, useState } from '@wordpress/element'
import { useCardField } from '../../hooks'
import {
  revolutSettings,
  PAYMENT_METHODS,
  i18n,
  dispatch,
  createAddress,
  createRevolutOrder,
  onPaymentSuccessHandler,
} from '../../utils'

const RevolutCardField = ({
  eventRegistration,
  billing,
  shippingData,
  shouldSavePayment,
  emitResponse,
  components,
  checkoutStatus,
}) => {
  const paymentMethod = PAYMENT_METHODS.REVOLUT_CARD
  const { onPaymentSetup, onCheckoutSuccess } = eventRegistration
  const settings = revolutSettings(paymentMethod)

  const rcRef = useRef()
  const [cardStatus, setCardStatus] = useState(false)
  const [cardErrors, setCardErrors] = useState([])
  const [isLoading, setLoading] = useState(false)
  const [revolutPublicId, setRevolutPublicId] = useState('')
  const [orderAmount, setOrderAmount] = useState(0)
  const { createErrorNotice, removeAllNotices } = dispatch('core/notices')

  useEffect(() => {
    const unsubscribeOnPaymentSetup = onPaymentSetup(handleOnPaymentSetup)
    const unsubscribeOnCheckoutSuccess = onCheckoutSuccess(handleOnPaymentSuccess)
    return () => {
      unsubscribeOnPaymentSetup()
      unsubscribeOnCheckoutSuccess()
    }
  }, [
    onCheckoutSuccess,
    onPaymentSetup,
    cardErrors,
    cardStatus,
    shouldSavePayment,
    billing.billingAddress,
    shippingData.shippingAddress,
  ])

  useEffect(() => {
    const updateOrder = async () => {
      if (billing?.cartTotal?.value) {
        setLoading(true)
        createRevolutOrder(settings)
          .then(json => {
            setRevolutPublicId(json.revolut_order_public_id)
            setOrderAmount(json.revolut_order_amount)
            setLoading(false)
          })
          .catch(err =>
            createErrorNotice(i18n(err.message || 'An unexpected error occurred'), {
              id: 'create_order_failed',
              context: emitResponse.noticeContexts.PAYMENTS,
            }),
          )
      }
    }

    updateOrder()
  }, [billing.cartTotal.value])

  const cardInputRef = useCardField(
    {
      publicId: revolutPublicId,
      locale: settings.locale,
      onMsg: msg => {
        switch (msg.type) {
          case 'payment_successful': {
            document.dispatchEvent(new Event('payment_successful'))
            break
          }
          case 'payment_failed': {
            document.dispatchEvent(
              new CustomEvent('payment_failed', { detail: msg.error.toString() }),
            )
            break
          }
          case 'instance_destroyed': {
            rcRef.current = null
            break
          }
          case 'instance_mounted': {
            rcRef.current = msg.instance
            break
          }
          case 'fields_errors_changed': {
            setCardErrors(msg.errors)
            break
          }
          case 'fields_status_changed': {
            setCardStatus(msg.status)
            break
          }
          default:
            break
        }
      },
    },
    [orderAmount],
  )

  const handleOnPaymentSetup = async () => {
    removeAllNotices()
    let errorMessage = null
    if (!billing?.billingAddress) {
      errorMessage = 'Please check your billing address, and retry again.'
    }

    if (!cardStatus.completed || cardErrors.length > 0) {
      errorMessage = 'The payment form is not ready for submission.'

      if (rcRef.current) {
        rcRef.current.validate()
        errorMessage =
          'The payment form is not ready for submission, please fix the errors below.'
      }
    }

    return errorMessage
      ? {
          type: emitResponse.responseTypes.ERROR,
          message: i18n(errorMessage),
          retry: true,
          messageContext: emitResponse.noticeContexts.PAYMENTS,
        }
      : {
          type: emitResponse.responseTypes.SUCCESS,
        }
  }

  const handleOnPaymentSuccess = async response => {
    setLoading(true)
    const { billingAddress } = billing
    const { shippingAddress } = shippingData

    const paymentData = {
      name: `${billingAddress.first_name} ${billingAddress.last_name}`,
      email: billingAddress.email,
      phone: billingAddress.phone,
      savePaymentMethodFor: shouldSavePayment ? 'merchant' : '',
    }

    if (billingAddress.country !== undefined && billingAddress.postcode !== undefined) {
      paymentData.billingAddress = createAddress(billingAddress)
      paymentData.shippingAddress = createAddress(billingAddress)
    }

    if (
      shippingAddress &&
      shippingAddress.country !== undefined &&
      shippingAddress.postcode !== undefined
    ) {
      paymentData.shippingAddress = createAddress(shippingAddress)
    }

    const paymentResult = await submitCard({ paymentData })
    if (paymentResult.success) {
      onPaymentSuccessHandler({
        response,
        paymentMethod: PAYMENT_METHODS.REVOLUT_CARD,
        shouldSavePayment: shouldSavePayment,
      })
      return
    }

    if (paymentResult.error) {
      setLoading(false)
      return {
        type: emitResponse.responseTypes.ERROR,
        message: i18n(
          paymentResult.error || 'Unexpected error occurred, please try again later',
        ),
        retry: true,
        messageContext: emitResponse.noticeContexts.PAYMENTS,
      }
    }
    setLoading(false)
  }

  const submitCard = async ({ paymentData }) => {
    rcRef.current.submit(paymentData)

    return new Promise((resolve, reject) => {
      document.addEventListener('payment_successful', () => {
        resolve({ success: true })
      })

      document.addEventListener('payment_failed', event => {
        resolve({ success: false, error: event.detail })
      })
    })
  }

  return (
    <>
      <components.LoadingMask
        showSpinner={true}
        isLoading={checkoutStatus.isProcessing || checkoutStatus.isComplete || isLoading}
      >
        <>
          {revolutPublicId && (
            <div id="woocommerce-revolut-card-element" ref={cardInputRef}></div>
          )}

          <div>
            {cardErrors.length > 0 &&
              cardErrors.map((error, key) => {
                return (
                  <li className="card-field-error" key={key}>
                    {error.message}
                  </li>
                )
              })}
          </div>
        </>
      </components.LoadingMask>
    </>
  )
}
export default RevolutCardField
